import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../context/AuthContext';
import { 
  BookOpen, Clock, Award, Sparkles, ArrowRight, 
  CheckCircle2, Loader2, GraduationCap
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TrainingCourses = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [courses, setCourses] = useState([]);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [coursesRes, progressRes] = await Promise.all([
        axios.get(`${API}/training/courses`),
        user ? axios.get(`${API}/training/progress`, { headers }) : Promise.resolve({ data: [] })
      ]);
      setCourses(coursesRes.data);
      setProgress(progressRes.data);
    } catch (error) {
      console.error('Failed to fetch training data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCourseProgress = (courseId) => {
    const p = progress.find(pr => pr.course?.id === courseId);
    if (!p) return { completed: 0, total: 0, percentage: 0 };
    return {
      completed: p.completed_count,
      total: p.total_lessons,
      percentage: Math.round((p.completed_count / p.total_lessons) * 100)
    };
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      {/* Hero Section */}
      <div className="pt-24 pb-12 bg-gradient-to-br from-primary/10 via-purple-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-6">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary">Free Training</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
            AI Tools Training
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Learn how to use the most powerful AI tools for your small business. 
            Free, practical, and beginner-friendly.
          </p>
        </div>
      </div>

      {/* Courses */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : courses.length > 0 ? (
          <div className="space-y-8">
            {courses.map((course) => {
              const courseProgress = getCourseProgress(course.id);
              return (
                <Card key={course.id} className="border-slate-200 overflow-hidden hover:border-primary/50 transition-colors">
                  <CardContent className="p-0">
                    <div className="flex flex-col lg:flex-row">
                      {/* Thumbnail */}
                      <div className="lg:w-80 h-48 lg:h-auto flex-shrink-0">
                        <img 
                          src={course.thumbnail_url || 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800'}
                          alt={course.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 p-6 flex flex-col">
                        <div className="flex flex-wrap gap-2 mb-3">
                          <Badge className="bg-green-100 text-green-800">Free</Badge>
                          <Badge variant="outline">{course.level}</Badge>
                          <Badge variant="outline">{course.lesson_count} Lessons</Badge>
                        </div>
                        
                        <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                          {course.title}
                        </h2>
                        <p className="text-slate-600 mb-4 flex-grow">
                          {course.description}
                        </p>

                        <div className="flex items-center gap-6 text-sm text-slate-500 mb-4">
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {course.total_duration_minutes} min total
                          </span>
                          <span className="flex items-center gap-1">
                            <BookOpen className="w-4 h-4" />
                            {course.lesson_count} lessons
                          </span>
                          {course.is_free && (
                            <span className="flex items-center gap-1 text-green-600">
                              <Award className="w-4 h-4" />
                              Certificate
                            </span>
                          )}
                        </div>

                        {/* Progress Bar (if logged in and started) */}
                        {user && courseProgress.total > 0 && courseProgress.completed > 0 && (
                          <div className="mb-4">
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-slate-600">Your Progress</span>
                              <span className="font-medium text-primary">
                                {courseProgress.completed}/{courseProgress.total} lessons
                              </span>
                            </div>
                            <Progress value={courseProgress.percentage} className="h-2" />
                          </div>
                        )}

                        <div className="flex items-center gap-4">
                          <Button 
                            onClick={() => navigate(`/training/${course.slug}`)}
                            className="bg-primary hover:bg-primary/90"
                          >
                            {courseProgress.completed > 0 ? 'Continue Learning' : 'Start Course'}
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                          {courseProgress.percentage === 100 && (
                            <span className="flex items-center gap-1 text-green-600 font-medium">
                              <CheckCircle2 className="w-5 h-5" />
                              Completed
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <GraduationCap className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No Courses Available</h3>
            <p className="text-slate-600">Check back soon for new training content!</p>
          </div>
        )}
      </div>

      {/* Benefits Section */}
      <div className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="font-heading text-2xl font-bold text-slate-900 text-center mb-12">
            What You'll Get
          </h2>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { icon: BookOpen, title: 'Step-by-Step Guides', desc: 'Clear instructions for every tool' },
              { icon: Sparkles, title: 'Copy/Paste Templates', desc: 'Ready-to-use prompts and templates' },
              { icon: Clock, title: 'Quick Wins', desc: '15-min projects for immediate results' },
              { icon: Award, title: 'Free Certificate', desc: 'Show your achievement' }
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">{item.title}</h3>
                <p className="text-sm text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrainingCourses;
